import { motion } from "framer-motion";
import { useSection, resolveUrl } from "../lib/useCMS";

const FALLBACK = {
  overline: "Philosophy",
  heading_top: "Greatness is",
  heading_accent: "Earned",
  heading_bottom: "not given.",
  description: "Through hard work, discipline, and purpose. With a focused 6:1 player-to-coach ratio, every athlete gets the attention, challenge, and individualized feedback needed to grow the right way.",
  image_url: "https://images.pexels.com/photos/6468938/pexels-photo-6468938.jpeg?auto=compress&cs=tinysrgb&w=1600",
  image_caption_overline: "Every Rep Has Purpose",
  image_caption: "Teaching hockey, not running ice time.",
  block_1_number: "01",
  block_1_title: "The Why",
  block_1_body: "Athletes learn the why behind every movement, so they think faster and react smarter under pressure.",
  block_2_number: "02",
  block_2_title: "The Standard",
  block_2_body: "Personal teaching, high standards, long-term excellence, not quick, meaningless reps.",
  manifesto_title: "Outwork. Outthink. Outperform.",
  manifesto_body: "The path to becoming elite isn't about flash or shortcuts. It's built on fundamentals, one brick at a time.",
};

export const Philosophy = () => {
  const p = useSection("philosophy", FALLBACK);

  return (
    <section id="philosophy" data-testid="philosophy-section" className="relative bg-[#0a0a0a] py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-x-0 top-0 pointer-events-none overflow-hidden">
        <div className="animate-marquee-reverse font-display text-[14rem] leading-none uppercase text-outline opacity-40 whitespace-nowrap -mt-6">
          BUILT DIFFERENT · BUILT DIFFERENT · BUILT DIFFERENT · BUILT DIFFERENT ·
        </div>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-x-0 gap-y-6 md:gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="overline">{p.overline}</p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="font-display text-5xl md:text-6xl lg:text-7xl uppercase mt-5 leading-[0.9]" data-testid="philosophy-heading">
              {p.heading_top}
              <br />
              <span className="text-brand-light">{p.heading_accent}</span>,<br />
              {p.heading_bottom}
            </motion.h2>
            <p className="mt-6 text-zinc-400 max-w-md">{p.description}</p>
          </div>

          <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-4 md:gap-5">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bento-card col-span-2 row-span-1 relative overflow-hidden aspect-[16/9]" data-testid="philosophy-bento-1">
              <img src={resolveUrl(p.image_url)} alt="Defenseman battling along the boards" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">{p.image_caption_overline}</div>
                <div className="font-display text-2xl md:text-3xl uppercase mt-2">{p.image_caption}</div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="bento-card p-6 md:p-8" data-testid="philosophy-bento-2">
              <div className="font-display text-5xl text-brand-light">{p.block_1_number}</div>
              <div className="font-display text-xl uppercase mt-3">{p.block_1_title}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.block_1_body}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} className="bento-card p-6 md:p-8" data-testid="philosophy-bento-3">
              <div className="font-display text-5xl text-brand-light">{p.block_2_number}</div>
              <div className="font-display text-xl uppercase mt-3">{p.block_2_title}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.block_2_body}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="bento-card p-6 md:p-8 col-span-2 bg-brand border-brand" data-testid="philosophy-bento-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                <div className="font-display text-3xl sm:text-4xl md:text-5xl uppercase text-white leading-[0.95] break-words">
                  {p.manifesto_title}
                </div>
                <div className="text-sm text-white/80 md:max-w-sm md:ml-auto">{p.manifesto_body}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
